import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, CircularProgress } from '@mui/material';
import { DailyReportForm } from '../../components/DailyReport/DailyReportForm';
import { dailyReportService } from '../../services/dailyReport';
import { constructionService } from '../../services/construction';
import { memberService } from '../../services/member';
import { ReportData, ConstructionInfo, ReportMemberData, ReportVehicleData, ReportRemarks, PrefillResponse } from '../../types/dailyReport';
import { Member } from '../../types/member';
import { Vendor } from '../../types/vendor';
import { VendorMember } from '../../types/vendor';
import { vendorService } from '../../services/vendor';
import { Car } from '../../types/car';
import { carService } from '../../services/car';
import { RouteInfo } from '../../types/address';
import { InvoiceRequestDialog } from '../../components/InvoiceRequest/InvoiceRequestDialog';

const DailyReportFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState<ReportData | null>(null);
  const [constructionInfo, setConstructionInfo] = useState<any>(null);
  const [members, setMembers] = useState<Member[]>([]); 
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorMembers, setVendorMembers] = useState<VendorMember[]>([]);
  const [cars, setCars] = useState<Car[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [routeInfo, setRouteInfo] = useState<RouteInfo | undefined>(undefined);
  const [prefillResponse, setPrefillResponse] = useState<PrefillResponse | null>(null);

  // 請求書作成依頼ダイアログ用の状態
  const [invoiceRequestDialogOpen, setInvoiceRequestDialogOpen] = useState(false);
  const [isCompletedChanged, setIsCompletedChanged] = useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // 編集モードの場合
        if (id) {
          const reportData = await dailyReportService.getById(Number(id));

          // データを変換してフォーム用の形式に整形
          const formattedData: ReportData = {
            constructionId: reportData.constructionId,
            date: formatDate(reportData.date),
            supervisor: reportData.supervisor,
            manPower: Number(reportData.manPower),
            mileage: Number(reportData.mileage),
            laborCost: reportData.laborCost,
            fuelCost: reportData.fuelCost,
            vehicleCost: reportData.vehicleCost,
            tollLoadFee: reportData.tollLoadFee,
            memberData: convertMemberData(reportData.memberData),
            vehicleData: convertVehicleData(reportData.vehicleData),
            remarkData: convertRemarksData(reportData.remarkData)
          };

          setInitialData(formattedData);
          const constructionId = reportData.constructionId;
          
          // 以下、既存の処理を活用
          const [
            constructionData, 
            membersData, 
            vendorsData, 
            vendorMembersData,
            carsData
          ] = await Promise.all([
            constructionService.getById(Number(constructionId)),
            memberService.getAllMembers(),
            vendorService.getAllVendors(),
            vendorService.getAllVendorMembers(),
            carService.getAllCars()
          ]);
          
          setConstructionInfo({
            constructionId: constructionData.ID,
            customerName: constructionData.customerName,
            siteName: constructionData.siteName,
            estimatedAmount: constructionData.estimatedAmount || 0,
            supervisor: constructionData.supervisor,
            address: constructionData.address,
            isCompleted: constructionData.isCompleted
          });
  
          setRouteInfo(constructionData.routeInfo || undefined);
          setMembers(membersData);
          setVendors(vendorsData);
          setVendorMembers(vendorMembersData);
          setCars(carsData);
        } 
        // 新規作成モードの場合（既存の処理をそのまま使用）
        else {
          const constructionId = searchParams.get('constructionId');
          if (!constructionId) {
            setError('工事IDが指定されていません');
            return;
          }

          const [
            constructionData,
            membersData,
            vendorsData,
            vendorMembersData,
            carsData,
            prefillData
          ] = await Promise.all([
            constructionService.getById(Number(constructionId)),
            memberService.getAllMembers(),
            vendorService.getAllVendors(),
            vendorService.getAllVendorMembers(),
            carService.getAllCars(),
            dailyReportService.getPrefill(Number(constructionId)).catch(() => null)
          ]);

          setConstructionInfo({
            constructionId: constructionData.ID,
            customerName: constructionData.customerName,
            siteName: constructionData.siteName,
            estimatedAmount: constructionData.estimatedAmount || 0,
            supervisor: constructionData.supervisor,
            address: constructionData.address,
            isCompleted: constructionData.isCompleted
          });

          setRouteInfo(constructionData.routeInfo || undefined);
          setMembers(membersData);
          setVendors(vendorsData);
          setVendorMembers(vendorMembersData);
          setCars(carsData);

          // プリフィルデータがあれば初期値に組み込む（職長・メンバー・車両のみ）
          if (prefillData && prefillData.prefill) {
            setPrefillResponse(prefillData);
            const today = (() => {
              const d = new Date();
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            })();
            setInitialData({
              constructionId: Number(constructionId),
              date: today,
              supervisor: prefillData.prefill.supervisor,
              manPower: 0,
              mileage: 0,
              laborCost: 0,
              fuelCost: 0,
              vehicleCost: 0,
              tollLoadFee: 0,
              memberData: convertMemberData(prefillData.prefill.memberData),
              vehicleData: convertVehicleData(prefillData.prefill.vehicleData),
              remarkData: []
            });
          }
        }
      } catch (error) {
        console.error('データの取得に失敗:', error);
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
  
    loadInitialData();
  }, [id, searchParams]);

  const handleSubmit = async (data: ReportData, isCompleted: boolean) => {
    try {
      let reportId;
      
      if (id) {
        // 編集モード
        await dailyReportService.update(Number(id), data);
        reportId = Number(id);
      } else {
        // 新規作成モード
        const result = await dailyReportService.create(data);
        reportId = result.id; // APIレスポンス形式に応じて調整
      }
    
      // 工事ステータスの更新
      await constructionService.updateStatus({
        constructionId: data.constructionId,
        isCompleted: isCompleted
      });
      
      // 工事完了フラグが true の場合、請求書作成依頼ダイアログを表示
      if (isCompleted) {
        // 工事が完了に変更された場合、ダイアログを表示
        setIsCompletedChanged(true);
        setInvoiceRequestDialogOpen(true);
        // ここで必要な情報を含むオブジェクトを返す
        return { reportId, success: true };
      }
      
      // 通常のフロー（未完了または他のケース）
      navigate('/daily-report');
      // ここでも必要な情報を含むオブジェクトを返す
      return { reportId, success: true };
    } catch (error) {
      console.error('保存に失敗:', error);
      setError('データの保存に失敗しました');
      // エラー時は失敗したことを示すオブジェクトを返す
      return { success: false, error };
    }
  };
  
  // 請求書作成依頼ダイアログ成功時のハンドラー
  const handleInvoiceRequestSuccess = () => {
    // ダイアログを閉じる
    setInvoiceRequestDialogOpen(false);
    // 日報一覧画面へ遷移
    navigate('/daily-report');
  };

  // ダイアログをキャンセルした時のハンドラー
  const handleInvoiceRequestClose = () => {
    setInvoiceRequestDialogOpen(false);
    // 日報一覧画面へ遷移
    navigate('/daily-report');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
  };

  // APIレスポンスの形式からフォーム用の形式に変換
  const convertMemberData = (apiMembers: any[]): ReportMemberData[] => {
    return apiMembers.map(member => ({
      memberOrder: member.Member_Order,
      memberName: member.Member_Name,
      isAllDate: Number(member.Is_AllDate),      // 明示的に数値変換
      workType: Number(member.Work_Type),         // 明示的に数値変換
      overtimeHours: Number(member.Overtime_Hours),
      isCountTarget: Number(member.Is_Count_Target),
      baseUnitPrice: Number(member.Base_Unit_Price),      // カラム名の確認
      calculatedUnitPrice: Number(member.Calculated_Unit_Price),  // カラム名の確認
      overtimePay: Number(member.Overtime_Pay),
      nightShiftPay: Number(member.Night_Shift_Pay)
    }));
  };

  const convertVehicleData = (apiVehicles: any[]): ReportVehicleData[] => {
    return apiVehicles.map(vehicle => ({
      vehicleOrder: vehicle.Vehicle_Order,
      carId: vehicle.Car_ID,
      unitPrice: Number(vehicle.Unit_Price),
      mileage: Number(vehicle.Mileage || 0),     // APIの値を使用
      tollFee: Number(vehicle.Toll_Fee || 0)     // APIの値を使用
    }));
  };

  const convertRemarksData = (apiRemarks: any[]): ReportRemarks[] => {
    return apiRemarks.map(remark => ({
      remarksOrder: remark.Remarks_Order,
      remarks: remark.Remarks
    }));
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" disableGutters sx={{ px: { xs: 0.5, sm: 2 } }}>
      <Paper sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 1, sm: 3 } }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          {id ? '日報編集' : '日報登録'}
        </Typography>
        <DailyReportForm
          onSubmit={handleSubmit}
          onCancel={() => navigate('/construction')}
          initialData={initialData}
          constructionInfo={constructionInfo}
          members={members}
          vendors={vendors}
          vendorMembers={vendorMembers}
          cars={cars}
          loading={loading}
          routeInfo={routeInfo}
          prefillResponse={!id ? prefillResponse : null}
          fetchPrefill={!id && constructionInfo ? async (reportId: number) => {
            const result = await dailyReportService.getPrefill(constructionInfo.constructionId, reportId);
            if (result) setPrefillResponse(result);
            return result;
          } : undefined}
          convertMemberData={convertMemberData}
          convertVehicleData={convertVehicleData}
        />
      </Paper>

      {/* 請求書作成依頼ダイアログ */}
      {constructionInfo && (
        <InvoiceRequestDialog
          open={invoiceRequestDialogOpen}
          onClose={handleInvoiceRequestClose}
          constructionId={constructionInfo.constructionId}
          onSuccess={handleInvoiceRequestSuccess}
        />
      )}
    </Container>
  );
};

export default DailyReportFormPage;